import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, ArrowLeft, Check, Globe, PenTool, Palette, Eye, CreditCard, ExternalLink, Upload, Plus, X, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [studioCreated, setStudioCreated] = useState(false);
  
  // Form data
  const [importUrl, setImportUrl] = useState('');
  const [importedData, setImportedData] = useState(null);
  const [studioData, setStudioData] = useState({
    name: '',
    slug: '',
    description: '',
    accent_color: '#FF3399'
  });
  
  // Manufacturer selection
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [loadingManufacturers, setLoadingManufacturers] = useState(false);
  
  // Custom wraps
  const [customWraps, setCustomWraps] = useState([]);
  const [showAddCustomWrap, setShowAddCustomWrap] = useState(false);
  const [newCustomWrap, setNewCustomWrap] = useState({
    name: '',
    image_url: '',
    finish: 'gloss',
    category: 'Custom'
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch manufacturers on mount
  useEffect(() => {
    const fetchManufacturers = async () => {
      setLoadingManufacturers(true);
      try {
        // Seed master catalog first
        await axios.post(`${API}/seed-master-catalog`);
        const res = await axios.get(`${API}/wraps/manufacturers`);
        setManufacturers(res.data.manufacturers || []);
      } catch (err) {
        console.error('Failed to fetch manufacturers:', err);
      } finally {
        setLoadingManufacturers(false);
      }
    };
    fetchManufacturers();
  }, []);

  const handleImportWebsite = async () => {
    if (!importUrl) {
      toast.error('Please enter a URL');
      return;
    }

    setImportLoading(true);
    try {
      const response = await axios.post(`${API}/import-website`, { url: importUrl });
      setImportedData(response.data);
      setStudioData(prev => ({
        ...prev,
        name: response.data.studio_name || prev.name,
        description: response.data.about_text?.slice(0, 200) || prev.description
      }));
      toast.success('Website imported! Review and edit below.');
      setStep(2);
    } catch (error) {
      toast.error('Failed to import website. You can set up manually.');
      setStep(2);
    } finally {
      setImportLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  };

  const handleNameChange = (name) => {
    setStudioData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name)
    }));
  };

  const handleCreateStudio = async () => {
    if (!studioData.name || !studioData.slug) {
      toast.error('Please enter studio name and URL');
      return;
    }

    setLoading(true);
    try {
      // Try to create studio
      try {
        await axios.post(`${API}/studios`, {
          name: studioData.name,
          slug: studioData.slug,
          description: studioData.description
        }, { headers: getAuthHeader() });
      } catch (createErr) {
        // If user already has a studio, that's fine - just continue
        if (createErr.response?.data?.detail === "User already has a studio") {
          console.log("User already has studio, continuing...");
        } else if (createErr.response?.data?.detail === "Studio slug already taken") {
          toast.error("This URL is already taken. Please choose a different one.");
          setLoading(false);
          return;
        } else {
          throw createErr;
        }
      }

      // Update branding and imported site data
      const updateData = {
        accent_color: studioData.accent_color
      };
      
      if (importedData) {
        updateData.about_text = importedData.about_text;
        updateData.services = importedData.services;
        updateData.phone = importedData.phone;
        updateData.email = importedData.email;
        updateData.social_links = importedData.social_links;
        updateData.gallery_images = importedData.gallery_images;
        
        if (importedData.original_html) {
          updateData.imported_html = importedData.original_html;
          updateData.imported_css = importedData.original_css;
          updateData.imported_url = importedData.original_url;
          updateData.use_imported_design = true;
        }
      }
      
      await axios.put(`${API}/studios/me`, updateData, { headers: getAuthHeader() });

      // Fetch studio data to get the slug
      const studioRes = await axios.get(`${API}/studios/me`, { headers: getAuthHeader() });
      setStudioData(prev => ({
        ...prev,
        slug: studioRes.data.slug || prev.slug
      }));

      setStudioCreated(true);
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update studio');
    } finally {
      setLoading(false);
    }
  };

  const handleManufacturerToggle = (manufacturerName) => {
    setSelectedManufacturers(prev => {
      if (prev.includes(manufacturerName)) {
        return prev.filter(m => m !== manufacturerName);
      } else {
        return [...prev, manufacturerName];
      }
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploadingImage(true);
    try {
      // Convert to base64 for preview and storage
      const reader = new FileReader();
      reader.onload = (event) => {
        setNewCustomWrap(prev => ({
          ...prev,
          image_url: event.target.result
        }));
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error('Failed to upload image');
      setUploadingImage(false);
    }
  };

  const handleAddCustomWrap = () => {
    if (!newCustomWrap.name || !newCustomWrap.image_url) {
      toast.error('Please provide a name and upload an image');
      return;
    }

    setCustomWraps(prev => [...prev, { ...newCustomWrap, id: Date.now() }]);
    setNewCustomWrap({
      name: '',
      image_url: '',
      finish: 'gloss',
      category: 'Custom'
    });
    setShowAddCustomWrap(false);
    toast.success('Custom wrap added!');
  };

  const handleRemoveCustomWrap = (id) => {
    setCustomWraps(prev => prev.filter(w => w.id !== id));
  };

  const handleSaveWraps = async () => {
    if (selectedManufacturers.length === 0 && customWraps.length === 0) {
      toast.error('Please select at least one manufacturer or add a custom wrap');
      return;
    }

    setLoading(true);
    try {
      // Save selected manufacturers
      if (selectedManufacturers.length > 0) {
        await axios.put(`${API}/wraps/selected-manufacturers`, {
          selected_manufacturers: selectedManufacturers
        }, { headers: getAuthHeader() });
      }

      // Save custom wraps
      for (const wrap of customWraps) {
        await axios.post(`${API}/wraps`, {
          name: wrap.name,
          image_url: wrap.image_url,
          finish: wrap.finish,
          category: wrap.category || 'Custom',
          manufacturer: 'Custom'
        }, { headers: getAuthHeader() });
      }

      toast.success('Wraps saved successfully!');
      setStep(4);
    } catch (error) {
      console.error('Save wraps error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save wraps');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewSite = () => {
    window.open(`/s/${studioData.slug}`, '_blank');
  };

  const handleGoToBilling = () => {
    navigate('/app/billing');
  };

  const handleSkipToDashboard = () => {
    navigate('/app');
  };

  const totalWrapsCount = selectedManufacturers.reduce((acc, m) => {
    const manufacturer = manufacturers.find(mf => mf.name === m);
    return acc + (manufacturer?.wrap_count || 0);
  }, 0) + customWraps.length;

  const steps = [
    { num: 1, label: 'Import', icon: Globe },
    { num: 2, label: 'Studio', icon: PenTool },
    { num: 3, label: 'Wraps', icon: Palette },
    { num: 4, label: 'Preview', icon: Eye },
    { num: 5, label: 'Publish', icon: CreditCard },
  ];

  return (
    <div className="max-w-2xl mx-auto" data-testid="onboarding-page">
      {/* Progress */}
      <div className="flex items-center justify-center gap-2 mb-12">
        {steps.map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div 
              className={`w-10 h-10 flex items-center justify-center transition-all ${
                step > s.num ? 'bg-[#FF3399] text-black' :
                step === s.num ? 'bg-[#FF3399] text-black' :
                'bg-zinc-800 text-zinc-500'
              }`}
            >
              {step > s.num ? <Check size={16} /> : <s.icon size={16} />}
            </div>
            {i < steps.length - 1 && (
              <div className={`w-12 h-0.5 mx-1 ${step > s.num ? 'bg-[#FF3399]' : 'bg-zinc-800'}`}></div>
            )}
          </div>
        ))}
      </div>

      {/* Step 1: Import */}
      {step === 1 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-black mb-2 text-center">Welcome! Let's set up your studio</h1>
          <p className="text-zinc-400 text-center mb-12">Import your existing website or start fresh</p>

          <div className="bg-zinc-900 border border-zinc-800 p-8 mb-8">
            <h2 className="font-bold mb-4 text-white">Import from existing website</h2>
            <p className="text-zinc-500 text-sm mb-4">
              We'll extract your studio name, services, images, and contact info automatically.
            </p>
            <div className="flex gap-2">
              <Input
                type="url"
                placeholder="https://your-studio-website.com"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                className="bg-zinc-800 border-zinc-700 h-12 flex-1"
                data-testid="import-url-input"
              />
              <Button
                onClick={handleImportWebsite}
                disabled={importLoading}
                className="bg-[#FF3399] text-black hover:bg-[#FF3399]/90 h-12 px-6 font-bold"
                data-testid="import-btn"
              >
                {importLoading ? <span className="spinner w-5 h-5"></span> : 'Import'}
              </Button>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setStep(2)}
              className="text-zinc-400 hover:text-[#FF3399] text-sm transition-colors"
              data-testid="skip-import-btn"
            >
              Skip and start from scratch →
            </button>
          </div>
        </motion.div>
      )}

      {/* Step 2: Studio Details */}
      {step === 2 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-black mb-2 text-center">Studio Details</h1>
          <p className="text-zinc-400 text-center mb-12">Basic info about your wrap studio</p>

          <div className="bg-zinc-900 border border-zinc-800 p-8 space-y-6">
            <div>
              <Label className="text-zinc-400 mb-2 block">Studio Name *</Label>
              <Input
                type="text"
                value={studioData.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Elite Wraps Berlin"
                className="bg-zinc-800 border-zinc-700 h-12"
                data-testid="studio-name-input"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Studio URL *</Label>
              <div className="flex items-center gap-2">
                <span className="text-zinc-500 text-sm">wrapviz.com/s/</span>
                <Input
                  type="text"
                  value={studioData.slug}
                  onChange={(e) => setStudioData({ ...studioData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                  placeholder="elite-wraps"
                  className="bg-zinc-800 border-zinc-700 h-12 flex-1"
                  data-testid="studio-slug-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Description</Label>
              <Input
                type="text"
                value={studioData.description}
                onChange={(e) => setStudioData({ ...studioData, description: e.target.value })}
                placeholder="Premium vehicle wrapping studio"
                className="bg-zinc-800 border-zinc-700 h-12"
                data-testid="studio-desc-input"
              />
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Accent Color</Label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={studioData.accent_color}
                  onChange={(e) => setStudioData({ ...studioData, accent_color: e.target.value })}
                  className="w-12 h-12 cursor-pointer border-0 bg-transparent"
                  data-testid="studio-color-input"
                />
                <Input
                  type="text"
                  value={studioData.accent_color}
                  onChange={(e) => setStudioData({ ...studioData, accent_color: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 h-12 w-32 font-mono"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep(1)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back
            </Button>
            <Button
              onClick={handleCreateStudio}
              disabled={loading || !studioData.name || !studioData.slug}
              className="bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold"
              data-testid="create-studio-btn"
            >
              {loading ? <span className="spinner w-5 h-5"></span> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Step 3: Select Wraps - NEW DESIGN */}
      {step === 3 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-3xl font-black mb-2 text-center">Select Your Wraps</h1>
          <p className="text-zinc-400 text-center mb-8">Choose the brands you work with and add any custom wraps</p>

          {/* Manufacturers Selection */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#FF3399]" />
              <h2 className="font-bold text-white">Select Manufacturer Brands</h2>
            </div>
            <p className="text-zinc-500 text-sm mb-4">
              Select the wrap brands you carry. All their wraps will be pre-loaded into your visualizer.
            </p>

            {loadingManufacturers ? (
              <div className="flex justify-center py-8">
                <div className="spinner w-8 h-8"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {manufacturers.map((mfr) => (
                  <label
                    key={mfr.name}
                    className={`flex items-center gap-3 p-4 border cursor-pointer transition-all ${
                      selectedManufacturers.includes(mfr.name)
                        ? 'border-[#FF3399] bg-[#FF3399]/10'
                        : 'border-zinc-800 bg-zinc-800/50 hover:border-zinc-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedManufacturers.includes(mfr.name)}
                      onChange={() => handleManufacturerToggle(mfr.name)}
                      className="w-5 h-5 accent-[#FF3399]"
                    />
                    <div className="flex-1">
                      <span className="font-bold text-white">{mfr.name}</span>
                      <span className="text-zinc-500 text-sm ml-2">({mfr.wrap_count} wraps)</span>
                    </div>
                    {selectedManufacturers.includes(mfr.name) && (
                      <Check className="w-5 h-5 text-[#FF3399]" />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Custom Wraps */}
          <div className="bg-zinc-900 border border-zinc-800 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-[#FF3399]" />
                <h2 className="font-bold text-white">Custom Wraps</h2>
              </div>
              <span className="text-zinc-500 text-sm">{customWraps.length} added</span>
            </div>

            {/* Custom wraps list */}
            {customWraps.length > 0 && (
              <div className="space-y-2 mb-4">
                {customWraps.map((wrap) => (
                  <div key={wrap.id} className="flex items-center gap-3 p-3 bg-zinc-800 border border-zinc-700">
                    <img 
                      src={wrap.image_url} 
                      alt={wrap.name}
                      className="w-12 h-12 object-cover border border-zinc-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-white">{wrap.name}</p>
                      <p className="text-zinc-500 text-sm capitalize">{wrap.finish}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveCustomWrap(wrap.id)}
                      className="text-zinc-500 hover:text-red-500 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add custom wrap form */}
            {showAddCustomWrap ? (
              <div className="border border-zinc-700 p-4 space-y-4">
                <div>
                  <Label className="text-zinc-400 mb-2 block text-sm">Wrap Image *</Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  {newCustomWrap.image_url ? (
                    <div className="relative inline-block">
                      <img 
                        src={newCustomWrap.image_url}
                        alt="Preview"
                        className="w-24 h-24 object-cover border border-zinc-700"
                      />
                      <button
                        onClick={() => setNewCustomWrap(prev => ({ ...prev, image_url: '' }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="flex items-center gap-2 px-4 py-3 border border-dashed border-zinc-700 text-zinc-400 hover:border-[#FF3399] hover:text-[#FF3399] transition-colors"
                    >
                      {uploadingImage ? (
                        <span className="spinner w-4 h-4"></span>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Upload wrap image
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 mb-2 block text-sm">Wrap Name *</Label>
                    <Input
                      type="text"
                      value={newCustomWrap.name}
                      onChange={(e) => setNewCustomWrap(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Carbon Black"
                      className="bg-zinc-800 border-zinc-700 h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 mb-2 block text-sm">Finish *</Label>
                    <select
                      value={newCustomWrap.finish}
                      onChange={(e) => setNewCustomWrap(prev => ({ ...prev, finish: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 h-10 px-3 text-white"
                    >
                      <option value="gloss">Gloss</option>
                      <option value="satin">Satin</option>
                      <option value="matte">Matte</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowAddCustomWrap(false)}
                    variant="ghost"
                    className="flex-1 text-zinc-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddCustomWrap}
                    disabled={!newCustomWrap.name || !newCustomWrap.image_url}
                    className="flex-1 bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold"
                  >
                    Add Wrap
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={() => setShowAddCustomWrap(true)}
                variant="outline"
                className="w-full border-zinc-700 text-zinc-400 hover:text-[#FF3399] hover:border-[#FF3399]"
              >
                <Plus className="mr-2 w-4 h-4" />
                Add Custom Wrap
              </Button>
            )}
          </div>

          {/* Summary */}
          <div className="bg-[#FF3399]/10 border border-[#FF3399]/30 p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-zinc-300">Total wraps in your visualizer:</span>
              <span className="text-2xl font-black text-[#FF3399]">{totalWrapsCount}</span>
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(2)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back
            </Button>
            <Button
              onClick={handleSaveWraps}
              disabled={loading || totalWrapsCount === 0}
              className="bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold"
              data-testid="save-wraps-btn"
            >
              {loading ? <span className="spinner w-5 h-5"></span> : <>Continue <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
          </div>
        </motion.div>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="w-20 h-20 bg-zinc-800 flex items-center justify-center mx-auto mb-8">
            <Eye className="w-10 h-10 text-[#FF3399]" />
          </div>
          
          <h1 className="text-3xl font-black mb-2">Preview Your Website</h1>
          <p className="text-zinc-400 mb-8">Your studio website is ready! Take a look before publishing.</p>

          <div className="bg-zinc-900 border border-zinc-800 p-8 mb-8">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="text-left flex-1">
                <p className="text-zinc-500 text-sm mb-1">Your studio URL:</p>
                <p className="font-mono text-lg text-white">{window.location.origin}/s/{studioData.slug}</p>
              </div>
            </div>
            
            <Button
              onClick={handlePreviewSite}
              className="w-full bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold py-6 mb-4"
              data-testid="preview-site-btn"
            >
              <ExternalLink className="mr-2 w-4 h-4" />
              Preview Your Website
            </Button>
            
            <p className="text-zinc-500 text-sm">
              Opens in a new tab. Your site is visible but the visualizer requires a subscription.
            </p>
          </div>

          <div className="flex justify-between">
            <Button variant="ghost" onClick={() => setStep(3)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back
            </Button>
            <Button
              onClick={() => setStep(5)}
              className="bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold"
            >
              Continue to Publish <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </motion.div>
      )}

      {/* Step 5: Publish */}
      {step === 5 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="w-20 h-20 bg-zinc-800 flex items-center justify-center mx-auto mb-8">
            <CreditCard className="w-10 h-10 text-[#FF3399]" />
          </div>
          
          <h1 className="text-3xl font-black mb-2">Activate Your Studio</h1>
          <p className="text-zinc-400 mb-8">Subscribe to enable the AI visualizer for your customers</p>

          <div className="bg-zinc-900 border border-zinc-800 p-8 mb-8">
            <div className="text-4xl font-black text-[#FF3399] mb-2">€49/month</div>
            <p className="text-zinc-500 mb-6">Everything you need to showcase your wraps</p>
            <ul className="text-left space-y-2 text-zinc-300 mb-6 max-w-sm mx-auto">
              <li className="flex items-center gap-2"><Check size={16} className="text-[#FF3399]" /> White-label studio website</li>
              <li className="flex items-center gap-2"><Check size={16} className="text-[#FF3399]" /> AI-powered visualizer</li>
              <li className="flex items-center gap-2"><Check size={16} className="text-[#FF3399]" /> Lead capture & management</li>
              <li className="flex items-center gap-2"><Check size={16} className="text-[#FF3399]" /> 400 visualizations/month</li>
            </ul>
          </div>

          <Button
            onClick={handleGoToBilling}
            className="bg-[#FF3399] text-black hover:bg-[#FF3399]/90 font-bold px-12 py-6 mb-4"
            data-testid="subscribe-btn"
          >
            Subscribe & Activate
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>

          <div className="mt-4">
            <button
              onClick={handleSkipToDashboard}
              className="text-zinc-500 text-sm hover:text-zinc-300"
            >
              Skip for now and explore dashboard →
            </button>
          </div>

          <div className="flex justify-start mt-8">
            <Button variant="ghost" onClick={() => setStep(4)} className="text-zinc-400 hover:text-white">
              <ArrowLeft className="mr-2 w-4 h-4" /> Back
            </Button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default OnboardingPage;
